import { type CreateGenerateId, type Rule, type StyleSheet } from 'jss';

import preset from 'jss-preset-default';

const DEFAULT_PREFIX = 'c';
const maxRules = 1e10;

const hashString = (value: string): string => {
    if (!value) {
        return '0';
    }
    let hash = 0;
    for (let i = 0; i < value.length; i++) {
        const char = value.charCodeAt(i);
        hash = (hash << 5) - hash + char;
    }
    // Convert to 32bit unsigned integer in base 36 and pad with "0" to ensure length is 7.
    return (hash >>> 0).toString(36).padStart(7, '0');
};

const getRuleBlueprint = (rule: Rule, sheet?: StyleSheet): string => {
    const meta = sheet?.options?.meta ?? '';
    const prefix = sheet?.options?.classNamePrefix ?? '';

    return `${rule.key}` + (meta ? `-${meta}` : '') + (prefix ? `-${prefix}` : '');
};

export const createStableGenerateId: CreateGenerateId = () => {
    const generatedIds = new Map<string, number>();

    return (rule, sheet) => {
        if (generatedIds.size > maxRules) {
            throw new Error(`[JSS] You might have a memory leak. Rule counter is at ${generatedIds.size}.`);
        }

        const prefix = sheet?.options.classNamePrefix || DEFAULT_PREFIX;

        const blueprint = getRuleBlueprint(rule, sheet);
        const hash = hashString(blueprint);
        const baseClassName = `${prefix}-${rule.key}-${hash}`;
        const counter = generatedIds.get(baseClassName) ?? 0;

        generatedIds.set(baseClassName, counter + 1);

        return counter === 0 ? baseClassName : `${baseClassName}-${counter}`;
    };
};

export const createStableJssOptions = () => ({
    ...preset(),
    createGenerateId: createStableGenerateId,
});
